import type {
  ISupplier,
  ISupplier_PricingIndicator,
  ISupplier_Type as Slug,
  ISupplierDetailVenue,
} from '@bridebook/models/source/models/Suppliers.types';
import type { IRelation } from '@bridebook/models/source/models/Suppliers/Relations.types';
import type { ISupplierCalendar } from '@bridebook/models/source/models/Suppliers/Calendars.types';
import type { ISupplier as IWSupplier } from '@bridebook/models/source/models/Weddings/Suppliers.types';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import type { IDatePicker } from './datepicker/types';
import { SearchBoostValues } from './types';
import { NamedQuery } from './search-suppliers/types';

export type {
  ISupplier_Budget as BudgetRange,
  ISupplier_Type as Slug,
} from '@bridebook/models/source/models/Suppliers.types';
export { SearchBoostValues } from '@bridebook/models/source/models/Suppliers/Admin/admin.types';
export type { IBookingFile } from './booking-files/types';
export type { IArticleTag, IArticleTags } from './constants/articleTags/types';
export type {
  IArticle,
  IArticleTerms,
  IPromotedArticle,
  IStrippedArticlePostCategory,
  IStrippedArticlePostTag,
} from './schemas/article/types';
export type { IStatic } from './schemas/static/types';
export type { SupplierACLSupplierType } from './schemas/supplierACLSupplier/types';
export type { SupplierACLUserType } from './schemas/supplierACLUser/types';
export type { WeddingProfileType } from './schemas/weddingProfile/types';
export type { WeddingProfileACLProfileType } from './schemas/weddingProfileACLProfile/types';
export type { WeddingProfileACLProfilesRootType } from './schemas/weddingProfileACLProfilesRoot/types';
export type { WeddingProfileACLUserType } from './schemas/weddingProfileACLUser/types';
export type { WeddingProfileAclUsersRootType } from './schemas/weddingProfileACLUsersRoot/types';

/**
 * Url-friendly slug versions.
 */
export type UrlFriendlySlug =
  | 'beauty-hair-makeup'
  | 'cakes'
  | 'catering'
  | 'decoration-hire'
  | 'dress-accessories'
  | 'entertainment'
  | 'florists'
  | 'marquee-hire'
  | 'menswear'
  | 'musicians-bands-djs'
  | 'photographers'
  | 'planners-toastmasters-celebrants'
  | 'rings-jewellery'
  | 'stationery'
  | 'transport'
  | 'venues'
  | 'videographers';

/* ############################################################################
 *  Guest Collector
 * ######################################################################### */

export type GuestCollectorFormData = {
  email: string;
  phone?: string;
  street0?: string;
  street1?: string;
  postalCode?: string;
  city?: string;
  country?: CountryCodes;
  message?: string;
};

/* ############################################################################
 *  Analytics
 * ######################################################################### */

export type TrackEventSource = 'web' | 'cms' | 'enquiryWidget';

export interface TrackEventBody {
  event: string;
  genericProps?: Record<string, any>;
  identifyProps?: Record<string, any>;
  specificEventProps?: Record<string, any>;
  source?: TrackEventSource;
  category?: string;
  contactedSupplier?: any;
  bookedSupplier?: any;
  collaboratingSupplierId?: string;
  viewedSupplierId?: string;
}

export interface TrackEventBodyCMS extends TrackEventBody {
  source: 'cms';
}

export interface TrackEventBodyEnquiryWidget extends TrackEventBody {
  source: 'enquiryWidget';
}

export interface TrackEventBodyWeb extends Omit<TrackEventBody, 'genericProps'> {
  genericProps: Record<string, any> &
    (
      | {
          anonymousId: string;
          userId?: never;
          weddingId?: never;
        }
      | {
          anonymousId?: string;
          userId: string;
          weddingId: string;
        }
    );
  source: 'web';
}

export interface IExperiment {
  activationEvent: string;
  description: string;
  id: string;
  name: string;
}

export interface IVariant extends IExperiment {
  variant: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
}

/* ############################################################################
 *  Supplier
 * ######################################################################### */

/**
 * A venue-local day with at least one free showround slot. The day is still bookable while
 * lastDeadline (last start minus the calendar's notice) is not in the past.
 */
export interface IShowroundDay {
  day: string;
  lastDeadline: string;
  starts: string[];
}

// Days and slots of a showround day range search that are bookable at search time
export interface IShowroundAvailabilityInRange {
  availableDaysCount: number;
  availableSlotsCount: number;
}

export interface IElasticResponseVenueFields {
  ac: boolean;
  accommodationRooms: number;
  affordable: boolean;
  africanCuisine: boolean;
  alcoholLicense: boolean;
  alternative: boolean;
  asian: boolean;
  asianCatering: boolean;
  asianCuisine: boolean;
  ballroom: boolean;
  bar: boolean;
  barCocktailService: boolean;
  barn: boolean;
  bbDiscountExpiryDate: null | string;
  beach: boolean;
  blankCanvas: boolean;
  bookingsCountLast52Weeks: number;
  bridalChangingFacilities: boolean;
  byobPermitted: boolean;
  capacityDining: number;
  caribbeanCuisine: boolean;
  castle: boolean;
  casual: boolean;
  childcare: boolean;
  chineseJapaneseCuisine: boolean;
  churchWithinWalkingDistance: boolean;
  cityHotelCityVenue: boolean;
  cityView: boolean;
  classic: boolean;
  conferenceCentre: boolean;
  confettiPermitted: boolean;
  corkageCharge: boolean;
  countryHouseManorHouse: boolean;
  coupleBudgetAvg: number;
  coupleBudgetAvgMinDiff: number;
  coupleBudgetMax: number;
  coupleBudgetMin: number;
  cruiseBoatYacht: boolean;
  customOfferExpiryDate: null | string;
  decorations: boolean;
  disabledAccess: boolean;
  easternEuropean: boolean;
  enquiriesCountLast4Weeks: number;
  exclusiveUse: boolean;
  externalCateringPermitted: boolean;
  fireworksPermitted: boolean;
  formal: boolean;
  gardenOutdoor: boolean;
  golfCourse: boolean;
  gradeListedBuilding: boolean;
  greatViews: boolean;
  groupParent: string;
  halalCatering: boolean;
  hasAccommodation: boolean;
  hasBadDebtCoupleside: boolean;
  hasBridebookAwards: boolean;
  hasBridebookOffer: boolean;
  hasIndustryAwards: boolean;
  hasQualityPricing: boolean;
  hasShowroundViewingDatesAvailable: boolean;
  showroundViewingDates: Array<{ start: string; end: string }>;
  showroundAvailableDays: string[];
  showroundAvailableSlotCounts: number[];
  showroundDays: IShowroundDay[];
  showroundMinAdvance?: ISupplierCalendar['minAdvance'];
  historic: boolean;
  hogRoast: boolean;
  hotel: boolean;
  indian: boolean;
  inHouseCatering: boolean;
  inHouseDancefloor: boolean;
  inHouseWeddingCoordinator: boolean;
  inHouseWineList: boolean;
  intimate: boolean;
  japanese: boolean;
  kosherCatering: boolean;
  landscapedGardens: boolean;
  latestSpecialOfferDate: null | string;
  latestWeddingFairDate: null | string;
  upcomingWeddingFairDates: string[];
  upcomingOpenDayDates: string[];
  latestOpenDayDate: null | string;
  latinSouthAmericanCuisine: boolean;
  lovelyGrounds: boolean;
  luxury: boolean;
  marqueePermitted: boolean;
  mediterranean: boolean;
  middleEasternCuisine: boolean;
  midweekOfferExpiryDate: null | string;
  modern: boolean;
  museumAttraction: boolean;
  onsiteParking: boolean;
  openFlamePermitted: boolean;
  otherVenueType: boolean;
  outdoor: boolean;
  outdoorCeremonyLicence: boolean;
  outdoorSpace: boolean;
  petFriendly: boolean;
  placeOfWorship: boolean;
  pricePerHeadMax: number;
  pricePerHeadMin: number;
  pubRestaurant: boolean;
  resort: boolean;
  romantic: boolean;
  rooftop: boolean;
  rustic: boolean;
  seasonalOfferExpiryDate: null | string;
  seoUrlSlug: string;
  shortlistsCountLastWeek: number;
  soundSystemAvailable: boolean;
  spaOnsite: boolean;
  sportingVenueStadium: boolean;
  stage: boolean;
  statelyHome: boolean;
  stylish: boolean;
  targetingBudget: {
    gte: number;
    lte: number;
  };
  targetingGuestsNumber: {
    gte: number;
    lte: number | null;
  };
  targetingDateD: IDatePicker['d'];
  targetingDateM: IDatePicker['m'];
  targetingDateY: IDatePicker['y'];
  tier: number;
  tierUpgradedInLast26Weeks: boolean;
  totalInclusivePriceMax: number;
  totalInclusivePriceMin: number;
  townHallRegistryOffice: boolean;
  transportation: boolean;
  trustedPartnerSince: number;
  unique: boolean;
  uniqueVenueType: boolean;
  unusual: boolean;
  urban: boolean;
  vegan: boolean;
  venueHirePriceMax: number;
  venueHirePriceMin: number;
  venueType: ISupplierDetailVenue['type'];
  videoToursCount: number;
  warehouseFactory: boolean;
  waterView: boolean;
  weddingLicence: boolean;
  westernEuropeanCuisine: boolean;
  wifi: boolean;
  winery: boolean;
  style: ISupplierDetailVenue['style'];
  relations: TElasticRelation[];
}

export type TElasticRelation = {
  supplierName: string;
  relationType: IRelation['type'];
  relationOrder: number;
  supplierCategory: Slug[];
  supplierId: string;
  supplierPublicId: string;
  address: {
    country: string;
    city: string;
    street: string[];
    postalCode: string;
    adminArea: string[];
  };
  countryCode: string;
};

export interface IElasticSupplier extends Partial<IElasticResponseVenueFields> {
  bestOfBritain: boolean;
  connections?: {
    recommendedBy?: string[];
    worksWith?: string[];
    recommends?: string[];
    relatedBy?: string[];
    relatesWith?: string[];
  };
  // @TODO: [i18n] Should this be typed as CountryCodes?
  country: string;
  countryCode: CountryCodes;
  county: string;
  currency: string;
  customArea: string;
  deleted: boolean;
  description: string;
  distanceFromOrigin: number;
  email: boolean;
  id: string;
  lat: number;
  lng: number;
  name: string;
  photos: string;
  postcode: string;
  profileScore: number;
  publicId: string;
  relations: TElasticRelation[];
  reviewsCount: number;
  reviewsOnlyCount: number;
  reviewsStars: number;
  seoScore: number;
  seoUrlSlug: string;
  statusMessage: string;
  testimonialsOnlyCount: number;
  thumbnail: string;
  timezone?: string;
  town: string;
  translations?: ISupplier['_translations'];
  type: Slug;
  matchedBy?: NamedQuery | string[] | null;
}

export interface IWeddingSupplier extends Omit<IWSupplier, 'createdAt' | 'updatedAt'> {
  createdAt?: number;
  updatedAt?: number;
  statusUpdate?: unknown;
}

export interface IUISupplier extends Partial<IElasticSupplier> {
  _score?: number;
  address?: { country: CountryCodes; adminArea: string };
  booked?: boolean;
  boost?: SearchBoostValues;
  capacityDining?: number;
  checked?: boolean;
  contacts?: { calendarUrl?: string; email?: boolean; phone?: string; website?: string };
  createdAt?: number;
  customSupplier?: boolean;
  detailedDescription?: string;
  distanceFromOrigin?: number;
  enquired?: boolean;
  featured?: boolean;
  hasAccommodation?: boolean;
  id: string;
  ignored?: unknown;
  insideBudget?: boolean;
  showroundAvailabilityInRange?: IShowroundAvailabilityInRange;
  isForeign?: boolean;
  l10n?: { country: CountryCodes; currency: string };
  lateAvailabilityDates?: string[] | null;
  maxWeddingPriceEstimate?: Range['max'];
  minWeddingPriceEstimate?: Range['min'];
  name: string;
  notes?: string;
  phone?: string;
  photosCount?: number;
  postcode?: string;
  price?: number;
  pricingIndicator?: ISupplier_PricingIndicator;
  professional?: unknown;
  publicId: string;
  registrationDate?: number;
  searchBoostForType?: string;
  seo?: { urlSlug: string };
  shortlisted?: unknown;
  showInfo?: boolean;
  slug?: Slug;
  statusUpdate?: string;
  supplierBoosted?: boolean;
  supplierPriceMax?: number;
  supplierPriceMin?: number;
  thumbext?: string;
  type: Slug;
  updatedAt?: number;
  videosCount?: number;
  visitDate?: string;
  website?: string;
  weddingsHosted?: number[];
}

export interface IRankHit {
  _index: string;
  _type: string;
  _id: string;
  _score: number | null;
  _source: {
    date: string;
    '@timestamp': string;
    RankUK?: number;
    county: string;
    id: string;
    countyRank: number;
  };
  sort: number[];
}

export interface IShapeHit {
  _index: string;
  _type: string;
  _id: string;
  _score: number | null;
  _source: {
    customArea: string;
  };
}

export type Awaited<T> = T extends PromiseLike<infer U> ? U : T;

// https://github.com/i18next/react-i18next/issues/1483#issuecomment-1268455602
export type TranslationObject = any;

export interface Action<P = any> {
  type: string;
  payload?: P;
}

export type ActionWithPayload<P> = Omit<Action, 'payload'> & { payload: P };

export interface IAddressComponents {
  locality: string;
  postal_code: string;
  postal_town: string;
  country: CountryCodes;
  administrative_area_level_2: string;
  administrative_area_level_1: string;
}

export interface INotificationObject {
  code?: string;
  message?: string | null;
  provider?: string | null;
  credential?: any;
}

export type { PartialRecursive as RecursivePartial } from '@bridebook/models/source/abstract/_';

export type Range = {
  /**
   * The minimum value of the range.
   */
  min: number;
  /**
   * The maximum value of the range.
   */
  max: number;
};

export const isIUISupplier = (supplier: unknown): supplier is IUISupplier =>
  Object.prototype.hasOwnProperty.call(supplier, 'countryCode');
